import { createContext, useContext, useReducer } from 'react';
import type { Dispatch } from 'react';

import { appStateReducer } from '../state/appState';
import type { AppAction, ColdAppState } from '../state/appState';
import type { SyncReactNode } from '../types';

// State and dispatch are split on purpose: dispatch is identity-stable, so
// components that only write never re-render on state changes. Viewport keys
// live elsewhere — they change every moveend and would re-render all consumers.

const AppStateContext = createContext<ColdAppState | null>(null);
const AppDispatchContext = createContext<Dispatch<AppAction> | null>(null);

type Props = {
  initialState: ColdAppState;
  children: SyncReactNode;
};

export const AppStateProvider = ({ initialState, children }: Props) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  return (
    <AppDispatchContext.Provider value={dispatch}>
      <AppStateContext.Provider value={state}>{children}</AppStateContext.Provider>
    </AppDispatchContext.Provider>
  );
};

export const useAppState = (): ColdAppState => {
  const state = useContext(AppStateContext);
  if (state === null) {
    throw new Error('useAppState must be used inside <AppStateProvider>');
  }
  return state;
};

export const useAppDispatch = (): Dispatch<AppAction> => {
  const dispatch = useContext(AppDispatchContext);
  if (dispatch === null) {
    throw new Error('useAppDispatch must be used inside <AppStateProvider>');
  }
  return dispatch;
};
